import React, { useEffect, useState } from 'react';
import {
  Avatar,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  TooltipProps,
  Typography,
  styled,
  tableCellClasses,
  tooltipClasses,
  useTheme,
} from '@mui/material';
import { Stack } from '@mui/system';
import {
  PristineBox,
  PristineTypography,
  PoisedBox,
  PoisedTypography,
  PassableBox,
  PassableTypography,
  PanickyBox,
  PanickyTypography,
} from '../../components/room/game/player-input/PrecisionGraphicIconsShared';
import {
  QuestionScoreDetails,
  QuestionStats,
} from '@par/shared/types/question-stats';

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    maxWidth: 500,
  },
}));

type PropTypes = {
  par?: number;
  questionStats: QuestionStats;
};

export const QuestionSeenStats = ({ par, questionStats }: PropTypes) => {
  const theme = useTheme();
  const [totalAnswersCount, setTotalAnswersCount] = useState<number>();

  useEffect(() => {
    if (!questionStats) return;
    const { scoreCounts } = questionStats;
    let total = 0;
    for (const val of Object.values(scoreCounts).map((sc) => sc.length)) {
      total = total + val;
    }
    setTotalAnswersCount(total);
  }, [questionStats]);

  const getPrecisionIcon = (fuzzyPenalty: number) => {
    if (fuzzyPenalty === -1) {
      return (
        <PristineBox>
          <PristineTypography>P</PristineTypography>
        </PristineBox>
      );
    }
    if (fuzzyPenalty === 0) {
      return (
        <PoisedBox>
          <PoisedTypography>P</PoisedTypography>
        </PoisedBox>
      );
    }
    if (fuzzyPenalty === 1) {
      return (
        <PassableBox>
          <PassableTypography>P</PassableTypography>
        </PassableBox>
      );
    }
    if (fuzzyPenalty === 2) {
      return (
        <PanickyBox>
          <PanickyTypography>P</PanickyTypography>
        </PanickyBox>
      );
    }
    return '-';
  };

  const questionScoreDetailsTooltip = (
    questionScoreDetails: QuestionScoreDetails[]
  ) => {
    return (
      <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography fontWeight={600} color="primary">
                    Player
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography
                    fontWeight={600}
                    sx={{ color: theme.palette.ranked.main }}
                  >
                    MMR
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600} color="primary">
                    Precision
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography fontWeight={600} color="primary">
                    Penalty
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {questionScoreDetails.map((e) => (
                <TableRow>
                  <TableCell>
                    <Typography>{e.username}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: theme.palette.ranked.main }}>
                      {e.rankedRating ? Math.floor(e.rankedRating) : 'calib..'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      {getPrecisionIcon(e.fuzzyPenalty)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography textAlign={'center'}>
                      {e.penalty ? (
                        <Avatar
                          sx={{
                            height: '25px',
                            width: '25px',
                            bgcolor: theme.palette.warning.main,
                            display: 'inline-flex',
                          }}
                        >
                          {e.penalty}
                        </Avatar>
                      ) : (
                        '-'
                      )}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Stack
      spacing={2}
      sx={{
        padding: '10px',
        borderRadius: '5px',
      }}
    >
      <Typography textAlign={'center'} fontWeight={600}>
        ANSWER STATS
      </Typography>
      <Typography variant="subtitle1" textAlign={'center'}>
        This question was seen <strong>{totalAnswersCount || 0}</strong> time
        {totalAnswersCount && totalAnswersCount === 1 ? '' : 's'}
      </Typography>
      <TableContainer component={Paper}>
        {questionStats ? (
          <Table size="small" aria-label="simple table">
            <TableHead>
              <TableRow style={{ backgroundColor: 'rgb(99 67 67)' }}>
                <TableCell>Seconds</TableCell>
                {Array.from({ length: 21 }, (_, i) => i).map((sec) => (
                  <TableCell
                    style={{
                      color:
                        par === sec ? theme.palette.primary.contrastText : '',
                      backgroundColor:
                        par === sec ? theme.palette.primary.light : '',
                    }}
                  >
                    {sec}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow
                sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
              >
                <TableCell># of answers</TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[0]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[0].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[0].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[1]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[1].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[1].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[2]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[2].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[2].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[3]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[3].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[3].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[4]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[4].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[4].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[5]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[5].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[5].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[6]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[6].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[6].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[7]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[7].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[7].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[8]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[8].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[8].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[9]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[9].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[9].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[10]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[10].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[10].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[11]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[11].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[11].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[12]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[12].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[12].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[13]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[13].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[13].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[14]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[14].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[14].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[15]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[15].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[15].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[16]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[16].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[16].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[17]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[17].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[17].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[18]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[18].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[18].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[19]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[19].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[19].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
                <TableCell>
                  <StyledTooltip
                    title={questionScoreDetailsTooltip(
                      questionStats.scoreCounts[20]
                    )}
                    placement="top"
                  >
                    <Typography textAlign={'center'} sx={{ cursor: 'pointer' }}>
                      {questionStats?.scoreCounts[20].length === 0
                        ? '-'
                        : questionStats?.scoreCounts[20].length}
                    </Typography>
                  </StyledTooltip>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        ) : null}
      </TableContainer>
      <Typography variant="caption" textAlign={'right'}>
        * Hover number for more details
      </Typography>
    </Stack>
  );
};
